import apiCaller from 'services/Api/ApiCaller';
import { API_URL } from 'settings';
import { CatalogueIndex } from './types';

export class CatalogueError extends Error {
  constructor(
    message: string,
    public status: number,
  ) {
    super(message);
    this.name = 'CatalogueError';
  }
}

// Local request state only. Nothing enters the shared underlying_assets store.
async function request<T>(
  path: string,
  method: 'GET' | 'POST' | 'PATCH' = 'GET',
  data: Record<string, unknown> = {},
  signal?: AbortSignal,
): Promise<T> {
  const response = await apiCaller({
    path,
    method,
    data,
    apiUrl: API_URL,
    signal,
  });
  if (response.status < 200 || response.status >= 300) {
    const body = response.result;
    const detail =
      typeof body === 'string'
        ? body
        : Array.isArray(body)
          ? body.join('; ')
          : body?.detail ||
            (body &&
              Object.entries(body)
                .map(([field, value]) => `${field}: ${String(value)}`)
                .join('; '));
    throw new CatalogueError(
      detail || 'The asset catalogue could not be loaded.',
      response.status,
    );
  }
  return response.result as T;
}

export function catalogueRequest<T>(
  path: string,
  method: 'GET' | 'POST' | 'PATCH' = 'GET',
  data: Record<string, unknown> = {},
  signal?: AbortSignal,
): Promise<T> {
  return request<T>(`/asset_catalogue/${path}`, method, data, signal);
}

// Reuse the existing guarded setting writer. Preparation itself stays read-only.
export async function enableFamilyRollover(
  sessionId: number,
  signal: AbortSignal,
) {
  const result = await request<{
    session_id: number;
    afs_bond_switchover_enabled: boolean;
  }>(
    `/sessions/${sessionId}/toggle_afs_bond_switchover`,
    'POST',
    { afs_bond_switchover_enabled: true },
    signal,
  );
  if (
    result?.session_id !== sessionId ||
    result.afs_bond_switchover_enabled !== true
  ) {
    throw new Error('Bond rollover could not be enabled. Please retry.');
  }
  return result;
}

export async function fetchCatalogue(
  scope: 'selection' | 'admin',
  signal?: AbortSignal,
  sessionId?: number,
) {
  const data = await catalogueRequest<CatalogueIndex>(
    'assets',
    'GET',
    { scope, ...(sessionId !== undefined ? { session_id: sessionId } : {}) },
    signal,
  );
  if (
    data?.schema_version !== 1 ||
    !Array.isArray(data.assets) ||
    !Array.isArray(data.terms) ||
    !Array.isArray(data.collections)
  ) {
    throw new Error('This catalogue response is not supported.');
  }
  return data;
}
